import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Switch, Route, Redirect } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import {
  trackProps,
  withTrack,
  withTrackProps,
} from "@dpdgroupuk/react-event-tracker";
import { compose } from "redux";

import AppHeader from "../../features/appHeader";
import SubHeader from "../../features/subHeader";
import Mapbox from "../../components/Mapbox";
import SectionMenu from "../../features/leftMenu";
import RouteDetail from "../../features/routeDetail";
import Notifier, { NotificationType } from "../../features/Notifier";
import { PAGE_START, ROUTES_PAGE, EMPTY_ROUTE } from "../../constants/routes";
import RouteListContainer from "./components/RouteListContainer";
import SectionListContainer from "./components/SectionListContainer";
import RouteFilter from "../routeFilter";
import { View } from "../../components/Base";
import LoadingOverlay from "../../components/LoadingOverlay";
import { isActiveLoadProcess } from "../../store/selectors/loader";
import { getRouteDetail } from "../../store/selectors/route";
import { fetchRouteDetail } from "../../store/actions/route";
import useLocationQueryParams from "../../hooks/useLocationQueryParams";
import { MAP_BOX, ROUTE_PAGE } from "../../constants/analytics";
import { getSelectedDepotLocation } from "../../store/selectors/app";
import {
  getCurrentUserId,
  hasDisasterRecoveryAccess,
  getDepotsForDropDown,
} from "../../store/selectors/user";
import { showNotification } from "../../store/actions/notifications";
import { DISMISS, NO_AVAILABLE_DEPOTS } from "../../constants/strings";
import usePolling from "../../hooks/usePolling";
import * as RouteService from "../../services/routes";
import * as SectionService from "../../services/section";
import { successRouteFetch } from "../../store/actions/routes";
import { successSectionsFetch } from "../../store/actions/sections";
import {
  LOAD_ROUTES_PROCESS,
  LOAD_SECTIONS_PROCESS,
} from "../../enums/loaderProcess";
import useLoader from "../../hooks/useLoader";
import RouteListProvider from "../../hooks/RouteList/Provider";

const useStyles = makeStyles({
  appContainer: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
  },
  content: {
    flexGrow: 1,
    position: "relative",
    display: "flex",
    overflow: "hidden",
  },
  mapbox: {
    position: "absolute",
    zIndex: 0,
    height: "100%",
    width: "100%",
  },
  left: {
    position: "relative",
    display: "flex",
  },
});

const PlannerPage = ({ handleOnMapLoad }) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const isLoadProcess = useSelector(isActiveLoadProcess);
  const routeDetail = useSelector(getRouteDetail);
  const depots = useSelector(getDepotsForDropDown);
  const { coordinates, zoom } = useSelector(getSelectedDepotLocation);
  const {
    routeId,
    detailVisible,
    date,
    depot,
    sectionId,
  } = useLocationQueryParams();
  const uid = useSelector(getCurrentUserId);
  const disasterRecoveryAccess = useSelector(hasDisasterRecoveryAccess);
  const [showLoader, hideLoader] = useLoader();
  const showRouteDetail = routeId && detailVisible && routeDetail;

  usePolling(
    depot ? [depot, sectionId, date, uid] : null,
    RouteService.getRoutes,
    {
      refreshInterval: 40000,
      onSuccess: (data) => {
        dispatch(successRouteFetch(data));
        hideLoader(LOAD_ROUTES_PROCESS);
      },
      onError: () => hideLoader(LOAD_ROUTES_PROCESS),
    }
  );

  usePolling(depot ? [depot, date, uid] : null, SectionService.getSection, {
    refreshInterval: 20000,
    onSuccess: (data) => {
      dispatch(successSectionsFetch(data));
      hideLoader(LOAD_SECTIONS_PROCESS);
    },
    onError: () => hideLoader(LOAD_SECTIONS_PROCESS),
  });

  useEffect(() => {
    showLoader(LOAD_ROUTES_PROCESS);
    showLoader(LOAD_SECTIONS_PROCESS);
  }, [date, depot, sectionId, dispatch, showLoader]);

  useEffect(() => {
    if (!depots.length) {
      dispatch(
        showNotification({
          type: NotificationType.ACTION,
          message: NO_AVAILABLE_DEPOTS,
          buttonText: DISMISS,
          key: "no-available-depots",
        })
      );
    }
  }, [depots, dispatch]);

  useEffect(() => {
    if (routeId && detailVisible) {
      dispatch(fetchRouteDetail(depot, routeId));
    }
  }, [depot, routeId, detailVisible, dispatch]);

  return (
    <RouteListProvider>
      <View className={styles.appContainer}>
        {isLoadProcess && <LoadingOverlay />}
        <AppHeader
          depots={depots}
          disasterRecoveryAccess={disasterRecoveryAccess}
        />
        <SubHeader />
        <>
          <View className={styles.content}>
            <View className={styles.mapbox}>
              <Mapbox
                handleOnLoad={handleOnMapLoad}
                coordinates={coordinates}
                zoom={zoom}
              />
            </View>
            {depots.length && (
              <>
                <View className={styles.left}>
                  <SectionMenu>
                    <Switch>
                      <Route
                        path={PAGE_START}
                        component={SectionListContainer}
                      />
                      <Route
                        path={ROUTES_PAGE}
                        component={RouteListContainer}
                      />
                      <Route exact path={EMPTY_ROUTE}>
                        <Redirect to={PAGE_START} />
                      </Route>
                    </Switch>
                  </SectionMenu>
                </View>
                <RouteFilter />
              </>
            )}
          </View>
          {showRouteDetail && <RouteDetail routeDetail={routeDetail} />}
        </>
        <Notifier />
      </View>
    </RouteListProvider>
  );
};

export default compose(
  withTrack(trackProps(ROUTE_PAGE)),
  withTrackProps({ handleOnMapLoad: MAP_BOX.LOAD })
)(PlannerPage);
